// Admin — Platform Content editor (Rules & Terms tab and any other page)
import React, { useCallback, useEffect, useState } from 'react';
import { Save, Trash2, Plus, Image as ImageIcon, Video } from 'lucide-react';
import { toast } from 'sonner';
import AdminShell from '../../components/admin/AdminShell';
import api from '../../utils/api';

const DEFAULT_PAGE = 'rules_terms';

export default function AdminPlatformContent() {
  const [pageKey, setPageKey] = useState(DEFAULT_PAGE);
  const [sections, setSections] = useState([]);
  const [editing, setEditing] = useState(null); // section being edited
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/platform-content/${pageKey}`);
      setSections(r.data.sections || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
  }, [pageKey]);
  useEffect(() => { load(); }, [load]);

  const newSection = () => setEditing({
    slug: `section-${Date.now()}`, title: '', body_md: '', images: [], video_urls: [], active: true, order: (sections.length + 1) * 10,
  });

  const save = async () => {
    if (!editing.title.trim()) { toast.error('Title required'); return; }
    setBusy(true);
    try {
      await api.post(`/admin/platform-content/${pageKey}`, editing);
      toast.success('✅ Section saved'); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const del = async (slug) => {
    if (!window.confirm(`Delete section "${slug}"?`)) return;
    try { await api.delete(`/admin/platform-content/${pageKey}/${slug}`); toast.success('✅ Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const uploadImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append('file', f);
    try {
      const r = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (r.data?.url) setEditing((s) => ({ ...s, images: [...(s.images || []), r.data.url] }));
    } catch (err) { toast.error('Upload failed'); }
    e.target.value = '';
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-4" data-testid="admin-platform-content">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-black">📄 Platform Content</h1>
            <p className="text-xs opacity-70">Edit Rules & Terms, About, FAQ etc — visible on marketplace tabs</p>
          </div>
          <div className="flex gap-2">
            <select value={pageKey} onChange={(e) => setPageKey(e.target.value)} data-testid="content-page-key"
                    className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
              <option value="rules_terms">Rules & Terms</option>
              <option value="about">About</option>
              <option value="faq">FAQ</option>
              <option value="privacy">Privacy</option>
            </select>
            <button onClick={newSection} data-testid="content-new-section"
                    className="px-3 py-1.5 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black flex items-center gap-1">
              <Plus size={12} /> New Section
            </button>
          </div>
        </div>

        {editing && (
          <div className="rounded-2xl bg-slate-900/70 border border-emerald-400/40 p-4 space-y-2" data-testid="content-edit-form">
            <div className="grid grid-cols-2 gap-2">
              <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="slug (unique)" data-testid="content-slug"
                     className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
              <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} placeholder="order" data-testid="content-order"
                     className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
            </div>
            <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Section title" data-testid="content-title"
                   className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
            <textarea value={editing.body_md} onChange={(e) => setEditing({ ...editing, body_md: e.target.value })} rows={6}
                      placeholder="Body text (Markdown supported — **bold**, *italic*, line breaks)"
                      data-testid="content-body"
                      className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm font-mono" />
            <div className="space-y-1">
              <div className="text-[10px] font-black opacity-70 flex items-center gap-1"><ImageIcon size={11} /> Images ({(editing.images || []).length})</div>
              <div className="flex flex-wrap gap-1">
                {(editing.images || []).map((img, i) => (
                  <div key={i} className="relative">
                    <img src={img} alt="" className="w-16 h-16 rounded object-cover border border-slate-600" />
                    <button onClick={() => setEditing({ ...editing, images: editing.images.filter((_, j) => j !== i) })}
                            className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-600 text-white text-[9px] font-black">×</button>
                  </div>
                ))}
                <label className="w-16 h-16 rounded border-2 border-dashed border-slate-600 hover:border-emerald-400 flex items-center justify-center cursor-pointer text-xs" data-testid="content-img-add">
                  <Plus size={16} /><input type="file" accept="image/*" onChange={uploadImage} className="hidden" />
                </label>
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-[10px] font-black opacity-70 flex items-center gap-1"><Video size={11} /> Video URLs (comma-separated YouTube/direct MP4)</div>
              <input value={(editing.video_urls || []).join(', ')} onChange={(e) => setEditing({ ...editing, video_urls: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                     placeholder="https://youtu.be/..., https://..." data-testid="content-videos"
                     className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="content-active" />
              Show on public tab
            </label>
            <div className="flex gap-2">
              <button onClick={() => setEditing(null)} className="flex-1 py-2 rounded bg-slate-700 text-xs font-black">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="content-save"
                      className="flex-1 py-2 rounded bg-emerald-500 text-slate-900 text-xs font-black flex items-center justify-center gap-1 disabled:opacity-50">
                <Save size={12} /> Save
              </button>
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          {sections.length === 0 && <div className="text-center py-8 opacity-60 text-xs">No sections yet. Click "New Section"</div>}
          {sections.map((s) => (
            <div key={s.slug} data-testid={`content-row-${s.slug}`} className="rounded-xl bg-slate-900/70 border border-slate-800 p-3">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-black">{s.title} {!s.active && <span className="text-[10px] text-rose-300 ml-1">(hidden)</span>}</div>
                  <div className="text-[10px] opacity-70 mt-0.5">
                    slug: {s.slug} · order: {s.order} · 📷 {(s.images || []).length} · 🎬 {(s.video_urls || []).length}
                  </div>
                </div>
                <button onClick={() => setEditing(s)} data-testid={`content-edit-${s.slug}`} className="px-2 py-1 rounded bg-slate-700 text-[10px] font-black">Edit</button>
                <button onClick={() => del(s.slug)} data-testid={`content-delete-${s.slug}`} className="px-2 py-1 rounded bg-rose-600 text-white text-[10px] font-black flex items-center gap-1">
                  <Trash2 size={10} /> Del
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminShell>
  );
}
